import type { OdysseyRead } from 'api/schema'
import type { RobotRead } from 'api/schema/models/RobotRead'
import { MAX_LIVE_EXECUTION_TRAIL_POINTS } from 'utils/constants'
import { capTrailPoints } from 'utils/robotTrajectory'

/** Pixel pose of GS robot on the floor map. Origin = bottom-left, `angle` deg, CCW positive, 0 = +x (right). */
export type RobotLiveExecutionMapPosition = {
  x: number
  y: number
  angle: number
}

export type RobotLiveExecutionTrailPoint = {
  x: number
  y: number
}

/** Ephemeral executing-task snapshot from `GET /api/main/robot/` (`live_execution`). Vendor-agnostic shape. */
export type RobotLiveExecution = {
  state: string | null
  nav_status?: string | null
  telemetry_state?: string | null
  reconciled_state?: string | null
  state_source?: string | null
  command_state?: string | null
  command_type?: string | null
  accepted_navigation_map?: string | null
  accepted_navigation_position?: string | null
  task_name: string | null
  map: {
    /** DB pk of cached `GausiumRobotMap` (if list sync has run). */
    id: number | null
    external_id: string | null
    name: string | null
    /** Cached preview PNG URL (relative `/storage/...`). Null until vendor preview is downloaded. */
    preview_url: string | null
    preview_fetched_at: string | null
    /** Pixel pose on the floor map; null if vendor didn't report `mapPosition`. */
    position: RobotLiveExecutionMapPosition | null
  } | null
  progress_percent: number | null
  time_remaining_seconds: number | null
  trail_points: RobotLiveExecutionTrailPoint[]
  trail_session_key: string | null
  recorded_at: string
  /** Opaque key for UI (e.g. correlates with `last_job_id` or a negative placeholder). */
  client_correlation_id: number
}

function parseMapPosition(raw: unknown): RobotLiveExecutionMapPosition | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const x = typeof r.x === 'number' ? r.x : Number(r.x)
  const y = typeof r.y === 'number' ? r.y : Number(r.y)
  const angle = typeof r.angle === 'number' ? r.angle : Number(r.angle)
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  return { x, y, angle: Number.isFinite(angle) ? angle : 0 }
}

export function parseRobotLiveExecution(raw: unknown): RobotLiveExecution | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const mapRaw = o.map
  const map =
    mapRaw && typeof mapRaw === 'object'
      ? {
          id:
            typeof (mapRaw as any).id === 'number' && Number.isFinite((mapRaw as any).id)
              ? ((mapRaw as any).id as number)
              : null,
          external_id: (mapRaw as any).external_id != null ? String((mapRaw as any).external_id) : null,
          name: (mapRaw as any).name != null ? String((mapRaw as any).name) : null,
          preview_url: (mapRaw as any).preview_url != null ? String((mapRaw as any).preview_url) : null,
          preview_fetched_at:
            (mapRaw as any).preview_fetched_at != null ? String((mapRaw as any).preview_fetched_at) : null,
          position: parseMapPosition((mapRaw as any).position),
        }
      : null
  const cid = o.client_correlation_id
  if (typeof cid !== 'number' || !Number.isFinite(cid)) return undefined
  return {
    state: o.state != null ? String(o.state) : null,
    nav_status: o.nav_status != null ? String(o.nav_status) : null,
    telemetry_state: o.telemetry_state != null ? String(o.telemetry_state) : null,
    reconciled_state: o.reconciled_state != null ? String(o.reconciled_state) : null,
    state_source: o.state_source != null ? String(o.state_source) : null,
    command_state: o.command_state != null ? String(o.command_state) : null,
    command_type: o.command_type != null ? String(o.command_type) : null,
    accepted_navigation_map: o.accepted_navigation_map != null ? String(o.accepted_navigation_map) : null,
    accepted_navigation_position:
      o.accepted_navigation_position != null ? String(o.accepted_navigation_position) : null,
    task_name: o.task_name != null ? String(o.task_name) : null,
    map,
    progress_percent:
      typeof o.progress_percent === 'number' && Number.isFinite(o.progress_percent) ? o.progress_percent : null,
    time_remaining_seconds:
      typeof o.time_remaining_seconds === 'number' && Number.isFinite(o.time_remaining_seconds)
        ? o.time_remaining_seconds
        : null,
    trail_points: capTrailPoints(
      Array.isArray(o.trail_points)
        ? o.trail_points
            .map(item => {
              if (!item || typeof item !== 'object') return null
              const x = Number((item as any).x)
              const y = Number((item as any).y)
              if (!Number.isFinite(x) || !Number.isFinite(y)) return null
              return { x, y }
            })
            .filter((item): item is RobotLiveExecutionTrailPoint => !!item)
        : [],
      MAX_LIVE_EXECUTION_TRAIL_POINTS
    ),
    trail_session_key: o.trail_session_key != null ? String(o.trail_session_key) : null,
    recorded_at: o.recorded_at != null ? String(o.recorded_at) : '',
    client_correlation_id: cid,
  }
}

/** Same `mission_progress` shape GS UI components used with synthetic odyssey rows. */
export function missionProgressFromLiveExecution(le: RobotLiveExecution | undefined): Record<string, unknown> {
  if (!le) return {}
  return {
    name: le.task_name,
    state: le.state,
    map_id: le.map?.external_id,
    map_name: le.map?.name,
    overall_mission_feedback: {
      progress: le.progress_percent,
      overall: 100,
      forecast: le.time_remaining_seconds,
    },
  }
}

export function readLiveExecutionFromRobotRead(robotRaw: RobotRead): RobotLiveExecution | undefined {
  return parseRobotLiveExecution((robotRaw as unknown as { live_execution?: unknown }).live_execution)
}

/** Minimal `OdysseyRead` for lists (Robot tasks) — not a DB row. */
export function odysseyStubFromLiveExecution(robotRaw: RobotRead, le: RobotLiveExecution): OdysseyRead {
  const sn = robotRaw.serial_number || ''
  const mission_status_last = {
    mission_progress: missionProgressFromLiveExecution(le),
  }
  const now = le.recorded_at || new Date().toISOString()
  return {
    id: le.client_correlation_id,
    name: le.task_name || le.map?.name || `Gausium task (${sn})`,
    status: 'new',
    description: '',
    user_id: robotRaw.user_id ?? null,
    created_at: now,
    updated_at: now,
    mission_status_last,
    job_type: null,
    scheduler_settings: null,
    missions: [],
    missions_info: [],
    robot_id: robotRaw.id,
    execution_time: null,
    predicted_duration: null,
    scenario: null,
    average_statistics: null,
  } as OdysseyRead
}
